/**
 * Its multipurpose messaging component using this we can create differnt types of messages like shipping banner.
 *
 * @module views/components/ValueMessaging
 * @memberof -Common
 */
import './ValueMessaging.scss';

import React from 'react';

import classNames from 'classnames';
import PropTypes from 'prop-types';

import Icon from '@ulta/core/components/Icon/Icon';
import Link_Huge from '@ulta/core/components/Link_Huge/Link_Huge';
import Markdown from '@ulta/core/components/Markdown/Markdown';
import { useAppConfigContext } from '@ulta/core/providers/AppConfigProvider/AppConfigProvider';
import { useDeviceInflection } from '@ulta/core/providers/InflectionProvider/InflectionProvider';

import { useTopBar } from '../TopBar/TopBar';

/**
 * Represents a ValueMessaging component
 *
 * @method
 * @param {ValueMessagingProps} props - React properties passed from composition
 * @returns ValueMessaging
 */
export const ValueMessaging = React.forwardRef( ( props, _ ) => {
  const { message, icon, linkAction, id } = props;
  const { displayValueMessaging, textColor } = useTopBar();
  const { enableDynamicPromoBar } = useAppConfigContext();
  const { inflection } = useDeviceInflection();

  if( !displayValueMessaging || !message ){
    return null;
  }

  return (
    <div className={ classNames(
      'ValueMessaging',
      { 'ValueMessaging--containerHeight': enableDynamicPromoBar }
    ) }
    >
      { enableDynamicPromoBar ? (
        <div className='ValueMessaging__messageContainer'>
          <div className='ValueMessaging__messageDynamic'>
            <Markdown
              id={ `${id}-markdown` }
              ariaHidden={ !!linkAction?.url }
              bodyStyle='body-3'
              bodyColor={ textColor }
              cmsMarkdownContent={ message }
            />
          </div>
          { !!icon && (
            <span className='ValueMessaging__icon'>
              <Icon
                size='lg'
                name={ icon }
                fill={ textColor }
              />
            </span>
          ) }
          { !!linkAction?.url && (
            <Link_Huge
              buttonPrimary
              action={ linkAction }
              className='ValueMessaging__link'
              ariaLabelledBy={ `${id}-markdown` }
            />
          ) }
        </div>
      ) : (
        <div className='ValueMessaging__message'>
          <Markdown
            bodyStyle='body-3'
            cmsMarkdownContent={ message }
          />
        </div>
      ) }
    </div>
  );

} );

/**
 * Property type definitions
 * @typedef ValueMessagingProps
 * @type {object}
 * @property {string} message - informative message for users
 */
export const propTypes =  {
  message: PropTypes.string.isRequired
};

ValueMessaging.propTypes = propTypes;
ValueMessaging.displayName = 'ValueMessaging';

export default ValueMessaging;
